import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { UserPlus, Edit2, Trash2, Lock, Unlock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle, Card, Badge, Button,
  Input, Select, Label, FormGroup, ModalOverlay, ModalBox, ModalHeader,
  ModalTitle, ModalBody, ModalFooter, Flex, LoadingContainer, Spinner
} from '../components/ui';
import { theme } from '../styles/theme';
import { getPasswordError } from '../utils/password';
import api from '../services/api';

const TenantUsers = () => {
  const { t } = useTranslation('users');
  const toast   = useToast();
  const confirm = useConfirm();
  const { tenantId } = useParams();
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData]     = useState({ name: '', email: '', role: 'agent', phone: '', password: '' });
  const [saving, setSaving]         = useState(false);
  const [tenant, setTenant]         = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSallusAdmin = user.role === 'sallus_admin';

  const ROLE_VARIANTS = { admin: 'warning', manager: 'info', agent: 'success' };

  const breadcrumbItems = [
    isSallusAdmin
      ? { label: 'Libben Agents', path: '/sallus/dashboard' }
      : user.role === 'manager'
        ? { label: t('home', { ns: 'common' }), path: `/manager/${tenantId}` }
        : { label: t('home', { ns: 'common' }), path: `/dashboard/${tenantId}` },
    { label: tenant?.name || tenantId, path: isSallusAdmin ? `/dashboard/${tenantId}` : undefined },
    { label: t('title') }
  ];

  useEffect(() => { loadUsers(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUsers = async () => {
    try {
      setLoading(true);
      const [tenantRes, membersRes] = await Promise.all([
        api.get(`/tenants/${tenantId}`),
        api.get(`/teams/${tenantId}/members`)
      ]);
      const tenant = tenantRes.data;
      setTenant(tenant);
      const adminUser = tenant.adminEmail
        ? [{ id: 'admin', name: tenant.adminName || 'Administrador', email: tenant.adminEmail, role: 'admin', status: 'active' }]
        : [];
      setUsers([...adminUser, ...(membersRes.data.members || [])]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAdd  = () => { setEditingUser(null); setFormData({ name: '', email: '', role: 'agent', phone: '', password: '' }); setShowModal(true); };
  const handleEdit = (u) => { setEditingUser(u); setFormData({ name: u.name, email: u.email, role: u.role, phone: u.phone || '', password: '' }); setShowModal(true); };

  const handleSave = async () => {
    if (!formData.name || !formData.email) { toast.warning(t('fillRequired')); return; }
    setSaving(true);
    try {
      if (editingUser?.id === 'admin') {
        const res = await api.put(`/tenants/${tenantId}`, { adminName: formData.name, adminEmail: formData.email });
        const updated = res.data.tenant;
        setUsers(users.map(u => u.id === 'admin' ? { ...u, name: updated.adminName, email: updated.adminEmail } : u));
      } else if (editingUser) {
        // Senha é opcional na edição; se preenchida, precisa atender à política.
        if (formData.password) {
          const pwErr = getPasswordError(formData.password);
          if (pwErr) { toast.warning(pwErr); setSaving(false); return; }
        }
        const res = await api.put(`/teams/${tenantId}/members/${editingUser.memberId}`, formData);
        setUsers(users.map(u => u.memberId === editingUser.memberId ? res.data.member : u));
      } else {
        if (!formData.password) { toast.warning(t('passwordRequired')); setSaving(false); return; }
        const pwErr = getPasswordError(formData.password);
        if (pwErr) { toast.warning(pwErr); setSaving(false); return; }
        const res = await api.post(`/teams/${tenantId}/members`, formData);
        setUsers([...users, res.data.member]);
      }
      setShowModal(false);
      toast.success(t('saveSuccess', { ns: 'common' }));
    } catch (e) {
      toast.error(t('saveError', { msg: e.response?.data?.message || e.message }));
    } finally { setSaving(false); }
  };

  const toggleStatus = async (u) => {
    if (u.id === 'admin') return;
    try {
      await api.put(`/teams/${tenantId}/members/${u.memberId}`, { status: u.status === 'active' ? 'inactive' : 'active' });
      setUsers(users.map(m => m.memberId === u.memberId ? { ...m, status: m.status === 'active' ? 'inactive' : 'active' } : m));
    } catch (e) { toast.error(t('toggleError', { msg: e.response?.data?.message || e.message })); }
  };

  const handleDelete = async (u) => {
    if (u.id === 'admin') { toast.warning(t('adminCannotRemove')); return; }
    const ok = await confirm({ title: t('removeConfirm', { name: u.name }), danger: true });
    if (!ok) return;
    try {
      await api.delete(`/teams/${tenantId}/members/${u.memberId}`);
      setUsers(users.filter(m => m.memberId !== u.memberId));
      toast.success(t('deleteSuccess', { ns: 'common' }));
    } catch (e) { toast.error(t('deleteError', { msg: e.response?.data?.message || e.message })); }
  };

  if (loading) return <LoadingContainer><Spinner size="24px" /> {t('loading')}</LoadingContainer>;

  return (
    <>
      <AppHeader title={t('title')} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>{t('title')}</PageTitle>
            <PageSubtitle>{t('subtitle', { count: users.length })}</PageSubtitle>
          </div>
          <Button variant="primary" onClick={handleAdd}>
            <UserPlus size={16} /> {t('addUser')}
          </Button>
        </PageHeader>

        <Card padding={false}>
          <TableHead>
            <Col flex="2">{t('colName')}</Col>
            <Col flex="2">{t('colEmail')}</Col>
            <Col flex="1">{t('colRole')}</Col>
            <Col flex="1">{t('colStatus')}</Col>
            <Col flex="1" align="right">{t('colActions')}</Col>
          </TableHead>
          {users.map(u => (
            <TableRow key={u.id || u.memberId}>
              <Col flex="2">
                <Avatar role={u.role}>{u.name?.[0]?.toUpperCase()}</Avatar>
                <UserName>{u.name}</UserName>
              </Col>
              <Col flex="2" style={{ color: theme.colors.textSecondary, fontSize: theme.typography.sm }}>{u.email}</Col>
              <Col flex="1">
                <Badge variant={ROLE_VARIANTS[u.role] || 'neutral'}>{t(`roles.${u.role}`) || u.role}</Badge>
              </Col>
              <Col flex="1">
                <Badge variant={u.status === 'active' ? 'success' : 'danger'}>
                  {u.status === 'active' ? t('active') : t('inactive')}
                </Badge>
              </Col>
              <Col flex="1" align="right">
                <Flex gap={theme.spacing[1]} justify="flex-end">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(u)}><Edit2 size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleStatus(u)}>
                    {u.status === 'active' ? <Lock size={14} /> : <Unlock size={14} />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(u)}><Trash2 size={14} /></Button>
                </Flex>
              </Col>
            </TableRow>
          ))}
        </Card>

        {showModal && (
          <ModalOverlay onClick={() => setShowModal(false)}>
            <ModalBox onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{editingUser ? t('editUser') : t('newUser')}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>✕</Button>
              </ModalHeader>
              <ModalBody>
                <FormGroup><Label>{t('fullName')}</Label><Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder={t('fullName')} /></FormGroup>
                <FormGroup><Label>{t('email')}</Label><Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder={t('email')} /></FormGroup>
                <FormGroup>
                  <Label>{t('role')}</Label>
                  <Select
                    value={formData.role}
                    onChange={e => setFormData({ ...formData, role: e.target.value })}
                    disabled={editingUser?.id === 'admin'}
                  >
                    <option value="agent">{t('roles.agent')}</option>
                    <option value="manager">{t('roles.manager')}</option>
                    <option value="admin">{t('roles.admin')}</option>
                  </Select>
                  {editingUser?.id === 'admin' && (
                    <RoleHint>{t('adminRoleHint', { defaultValue: 'O administrador principal não pode ter o perfil alterado.' })}</RoleHint>
                  )}
                </FormGroup>
                <FormGroup><Label>{t('phone')}</Label><Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="+55 11 98888-9999" /></FormGroup>
                {!editingUser && (
                  <FormGroup><Label>{t('password')}</Label><Input type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} placeholder={t('passwordPlaceholder')} /></FormGroup>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={() => setShowModal(false)}>{t('cancel', { ns: 'common' })}</Button>
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                  {saving ? <><Spinner size="14px" /> {t('saving')}</> : t('save')}
                </Button>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}
      </PageContainer>
    </>
  );
};

const BreadcrumbBar = styled.div`background: ${theme.colors.white}; padding: 0 ${theme.spacing[6]}; border-bottom: 1px solid ${theme.colors.border};`;
const TableHead = styled.div`display: flex; align-items: center; padding: ${theme.spacing[3]} ${theme.spacing[5]}; background: ${theme.colors.gray50}; border-bottom: 2px solid ${theme.colors.border};`;
const TableRow = styled.div`display: flex; align-items: center; padding: ${theme.spacing[3]} ${theme.spacing[5]}; border-bottom: 1px solid ${theme.colors.gray50}; transition: ${theme.transitions.fast}; &:hover { background: ${theme.colors.gray50}; } &:last-child { border-bottom: none; }`;
const Col = styled.div`flex: ${p => p.flex || 1}; display: flex; align-items: center; gap: ${theme.spacing[3]}; font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textMuted}; text-transform: uppercase; letter-spacing: 0.05em; justify-content: ${p => p.align === 'right' ? 'flex-end' : 'flex-start'};`;
const Avatar = styled.div`width: 32px; height: 32px; border-radius: ${theme.radii.full}; background: ${p => p.role === 'admin' ? theme.colors.warning : p.role === 'manager' ? theme.colors.info : theme.colors.primary}; color: white; display: flex; align-items: center; justify-content: center; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.bold}; flex-shrink: 0;`;
const UserName = styled.span`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; color: ${theme.colors.textPrimary};`;
const RoleHint = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: ${theme.spacing[1]};`;

export default TenantUsers;
